import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { api, errMsg } from "../lib/api";
import { inr, num, todayIST, GSTS } from "../lib/format";
import { StickyBar, Field, PageHead } from "../components/FormBits";

const NumIn = ({ value, onChange, testid }) => (
  <input className="field mono" type="number" inputMode="decimal" min="0" step="any" value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid} />
);

const LocationSelect = ({ value, onChange }) => {
  const [locs, setLocs] = useState([]);
  useEffect(() => {
    api.get("/locations").then((r) => setLocs(r.data));
  }, []);
  const add = async () => {
    const name = window.prompt("New godown / showroom name");
    if (!name?.trim()) return;
    try {
      const { data } = await api.post("/locations", { name: name.trim(), kind: /show/i.test(name) ? "Showroom" : "Godown" });
      setLocs((l) => [...l, data].sort((a, b) => a.name.localeCompare(b.name)));
      onChange(data.name);
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <select className="field" value={value} onChange={(e) => (e.target.value === "__add" ? add() : onChange(e.target.value))} data-testid="receipt-location-select">
      <option value="">Select location…</option>
      {locs.map((l) => <option key={l.id} value={l.name}>{l.name} ({l.kind})</option>)}
      <option value="__add">+ Add new location…</option>
    </select>
  );
};

function useJob(id) {
  const [job, setJob] = useState(null);
  useEffect(() => {
    api.get(`/jobs/${id}`).then((r) => setJob(r.data)).catch((e) => toast.error(errMsg(e)));
  }, [id]);
  return job;
}

export function JobReceiptForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const job = useJob(id);
  const [f, setF] = useState({ date: todayIST(), location: "", remarks: "" });
  const [q, setQ] = useState({});
  const [saving, setSaving] = useState(false);
  if (!job) return <p className="text-muted-foreground">Loading…</p>;
  const setQty = (lid, k, v) => setQ((x) => ({ ...x, [lid]: { ...x[lid], [k]: v } }));
  const good = job.items.reduce((a, l) => a + (Number(q[l.id]?.good_pcs) || 0), 0);

  const save = async () => {
    if (!f.location) return toast.error("Select where the shirts were received");
    setSaving(true);
    try {
      await api.post(`/jobs/${id}/receipts`, { ...f, items: job.items.map((l) => ({ job_item_id: l.id,
        good_pcs: Number(q[l.id]?.good_pcs) || 0, rejected_pcs: Number(q[l.id]?.rejected_pcs) || 0, leftover_returned: Number(q[l.id]?.leftover_returned) || 0 })) });
      toast.success("Receipt saved");
      navigate("/jobs", { state: { open: id } });
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="receipt-form">
      <PageHead kicker={`${job.job_no} · ${job.fabricator.name}`} title="Receive shirts" sub="Record good shirts, rejected shirts and leftover fabric returned." />
      <section className="panel rise grid grid-cols-1 gap-4 p-4 min-[400px]:grid-cols-2">
        <Field label="Date"><input type="date" className="field" value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} data-testid="receipt-date-input" /></Field>
        <Field label="Received at *"><LocationSelect value={f.location} onChange={(v) => setF({ ...f, location: v })} /></Field>
      </section>
      {job.items.map((l, i) => (
        <div key={l.id} className="panel rise space-y-3 p-4" data-testid={`receipt-line-${i}`}>
          <div>
            <p className="font-semibold">{l.description} {l.code && <span className="mono text-xs text-muted-foreground">[{l.code}]</span>}</p>
            <p className="text-xs text-muted-foreground">{l.style || "—"} · issued {num(l.qty_issued)} {l.uom} · expected {num(l.expected_pcs)} · pending <b>{num(l.pending_pcs)}</b> shirts</p>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <Field label="Good shirts"><NumIn value={q[l.id]?.good_pcs ?? ""} onChange={(v) => setQty(l.id, "good_pcs", v)} testid={`receipt-line-${i}-good-input`} /></Field>
            <Field label="Rejected"><NumIn value={q[l.id]?.rejected_pcs ?? ""} onChange={(v) => setQty(l.id, "rejected_pcs", v)} testid={`receipt-line-${i}-rejected-input`} /></Field>
            <Field label={`Leftover ${l.uom}`}><NumIn value={q[l.id]?.leftover_returned ?? ""} onChange={(v) => setQty(l.id, "leftover_returned", v)} testid={`receipt-line-${i}-leftover-input`} /></Field>
          </div>
          {(Number(q[l.id]?.good_pcs) || 0) + (Number(q[l.id]?.rejected_pcs) || 0) > l.pending_pcs && (
            <p className="text-xs font-semibold text-amber-600 dark:text-amber-400" data-testid={`receipt-line-${i}-over`}>More shirts than pending ({num(l.pending_pcs)}). Check the quantity before saving.</p>
          )}
        </div>
      ))}
      <section className="panel p-4"><Field label="Remarks"><textarea className="field" rows={2} value={f.remarks} onChange={(e) => setF({ ...f, remarks: e.target.value })} data-testid="receipt-remarks-input" /></Field></section>
      <StickyBar onSave={save} saving={saving} label="Save receipt" testid="receipt">
        <p className="mono-label text-muted-foreground">Good shirts</p>
        <p className="mono text-xl font-bold" data-testid="receipt-total-good">{num(good)}</p>
      </StickyBar>
    </div>
  );
}

export function JobBillForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const job = useJob(id);
  const [f, setF] = useState({ bill_no: "", bill_date: todayIST(), gst: 5, remarks: "" });
  const [lines, setLines] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (job) setLines(Object.fromEntries(job.items.map((l) => [l.id, { pcs: Math.max(l.good_pcs - l.billed_pcs, 0) || "", rate: l.rate || "" }])));
  }, [job]);
  if (!job || !lines) return <p className="text-muted-foreground">Loading…</p>;
  const taxable = job.items.reduce((a, l) => a + (Number(lines[l.id].pcs) || 0) * (Number(lines[l.id].rate) || 0), 0);
  const tax = (taxable * f.gst) / 100;

  const save = async () => {
    if (!f.bill_no.trim()) return toast.error("Enter the fabricator's bill number");
    setSaving(true);
    try {
      await api.post(`/jobs/${id}/bills`, { ...f, items: job.items.map((l) => ({ job_item_id: l.id, pcs: Number(lines[l.id].pcs) || 0, rate: Number(lines[l.id].rate) || 0 })) });
      toast.success("Fabricator bill saved");
      navigate("/jobs", { state: { open: id } });
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="bill-form">
      <PageHead kicker={`${job.job_no} · ${job.fabricator.name}`} title="Fabricator job-work bill" />
      <section className="panel rise grid grid-cols-2 gap-4 p-4">
        <Field label="Bill no *"><input className="field" value={f.bill_no} onChange={(e) => setF({ ...f, bill_no: e.target.value })} data-testid="bill-no-input" /></Field>
        <Field label="Bill date"><input type="date" className="field" value={f.bill_date} onChange={(e) => setF({ ...f, bill_date: e.target.value })} data-testid="bill-date-input" /></Field>
        <div className="col-span-2">
          <span className="label">GST %</span>
          <div className="seg">{GSTS.map((g) => <button type="button" key={g} data-active={f.gst === g} onClick={() => setF({ ...f, gst: g })} data-testid={`bill-gst-${g}`}>{g}%</button>)}</div>
        </div>
      </section>
      {job.items.map((l, i) => {
        const pcs = Number(lines[l.id].pcs) || 0;
        const set = (k) => (v) => setLines({ ...lines, [l.id]: { ...lines[l.id], [k]: v } });
        return (
          <div key={l.id} className="panel rise space-y-3 p-4" data-testid={`bill-line-${i}`}>
            <p className="font-semibold">{l.description} <span className="text-xs text-muted-foreground">{l.style}</span></p>
            <p className="text-xs text-muted-foreground">Good shirts received {num(l.good_pcs)} · already billed {num(l.billed_pcs)}</p>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Shirts billed"><NumIn value={lines[l.id].pcs} onChange={set("pcs")} testid={`bill-line-${i}-pcs-input`} /></Field>
              <Field label="Rate ₹ / shirt"><NumIn value={lines[l.id].rate} onChange={set("rate")} testid={`bill-line-${i}-rate-input`} /></Field>
            </div>
            {pcs + l.billed_pcs > l.good_pcs && <p className="text-xs font-semibold text-destructive" data-testid={`bill-line-${i}-over`}>Billed more shirts than good shirts received</p>}
          </div>
        );
      })}
      <section className="panel p-4"><Field label="Remarks"><textarea className="field" rows={2} value={f.remarks} onChange={(e) => setF({ ...f, remarks: e.target.value })} data-testid="bill-remarks-input" /></Field></section>
      <StickyBar onSave={save} saving={saving} label="Save bill" testid="bill">
        <p className="mono truncate text-xs text-muted-foreground">{inr(taxable)} + GST {inr(tax)}</p>
        <p className="mono truncate text-xl font-bold" data-testid="bill-total">{inr(taxable + tax)}</p>
      </StickyBar>
    </div>
  );
}
